import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EtatAppareil } from '@genviz/shared/enums';

import { useCarteSupervision, useExploitations } from '../api/queries';
import type { MarqueurSupervision } from '../api/types';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import { TUILES_URL } from '../config';
import { BadgeConnectivite, BadgeEtatAppareil, PastilleNombre } from '../components/Badges';
import { Bouton } from '../components/Boutons';
import { Carte, Definitions } from '../components/Carte';
import { Selecteur } from '../components/Champs';
import { CarteGeo } from '../components/CarteGeo';
import { Chargement } from '../components/Chargement';
import { MessageErreur } from '../components/MessageErreur';
import { LIBELLE_ETAT_APPAREIL, VALEUR_ABSENTE } from '../i18n/fr';
import { classes } from '../lib/divers';
import { formaterCoordonnees, formaterDateHeure, formaterNombre, formaterRelatif } from '../lib/format';

const ETATS: EtatAppareil[] = [
  EtatAppareil.NOMINAL,
  EtatAppareil.ATTENTION,
  EtatAppareil.CRITIQUE,
  EtatAppareil.ETEINT,
];

/**
 * Carte de supervision — tout le parc, toutes exploitations confondues.
 *
 * Un cas d'usage précis la justifie : repérer une panne CORRÉLÉE géographiquement (coupure
 * d'un opérateur mobile, orage sur un secteur) que les fiches appareil, prises une à une, ne
 * peuvent pas révéler. D'où le filtre par état, qui permet d'afficher les seuls appareils
 * critiques et de voir immédiatement s'ils sont groupés ou dispersés.
 */
export function SupervisionPage(): JSX.Element {
  const naviguer = useNavigate();

  const [exploitationId, setExploitationId] = useState('');
  const [etatsActifs, setEtatsActifs] = useState<ReadonlySet<EtatAppareil>>(new Set(ETATS));
  const [selectionne, setSelectionne] = useState<MarqueurSupervision | null>(null);

  const filtres = useMemo(
    () => ({
      ...(exploitationId ? { exploitation_id: exploitationId } : {}),
      ...(etatsActifs.size < ETATS.length ? { etats: [...etatsActifs] } : {}),
    }),
    [exploitationId, etatsActifs],
  );

  const carte = useCarteSupervision(filtres);
  // Le filtre par exploitation n'est peuplé que si l'opérateur a le droit de voir les clients :
  // sinon la requête partirait pour revenir en 403, sans rien apporter à l'écran.
  const peutVoirClients = usePeut(Capacite.CONSULTER_EXPLOITATIONS);
  const exploitations = useExploitations({}, { actif: peutVoirClients });

  const appareils = carte.data?.appareils ?? [];

  const compteurs = useMemo(() => {
    const compte: Record<EtatAppareil, number> = {
      [EtatAppareil.NOMINAL]: 0,
      [EtatAppareil.ATTENTION]: 0,
      [EtatAppareil.CRITIQUE]: 0,
      [EtatAppareil.ETEINT]: 0,
    };
    for (const a of appareils) compte[a.statut_metier] += 1;
    return compte;
  }, [appareils]);

  const basculerEtat = (etat: EtatAppareil): void => {
    setEtatsActifs((precedents) => {
      const suivants = new Set(precedents);
      if (suivants.has(etat)) suivants.delete(etat);
      else suivants.add(etat);
      // Ne jamais tout décocher : une carte vide n'apprend rien, elle donne l'illusion d'un parc vide.
      return suivants.size === 0 ? new Set(ETATS) : suivants;
    });
  };

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Carte du parc</h1>
          <span className="gv-corps-s gv-secondaire">
            {formaterNombre(appareils.length)} appareil(s) localisé(s)
            {carte.data && carte.data.sans_position > 0
              ? ` · ${formaterNombre(carte.data.sans_position)} sans position connue`
              : ''}
            {carte.data ? ` · relevé du ${formaterDateHeure(carte.data.genere_at)}` : ''}
          </span>
        </div>
        <div className="gv-entete__actions">
          <Bouton
            occupe={carte.isFetching}
            onClick={() => {
              void carte.refetch();
            }}
          >
            Actualiser
          </Bouton>
        </div>
      </header>

      {TUILES_URL === null ? (
        <div className="gv-bandeau gv-bandeau--info">
          Aucun fond cartographique n’est configuré (VITE_MAP_TILES_URL). Les marqueurs restent
          positionnés les uns par rapport aux autres, ce qui suffit à repérer une panne groupée ;
          renseignez un gabarit de tuiles pour ajouter un fond de plan.
        </div>
      ) : null}

      <Carte>
        <div className="gv-filtres">
          {peutVoirClients ? (
            <Selecteur
              label="Exploitation"
              optionVide="Toutes les exploitations"
              options={(exploitations.data?.items ?? []).map((e) => ({
                valeur: e.id,
                libelle: e.nom,
              }))}
              value={exploitationId}
              onChange={(e) => {
                setExploitationId(e.currentTarget.value);
                setSelectionne(null);
              }}
            />
          ) : null}

          <div className="gv-champ">
            <span className="gv-champ__label">États affichés</span>
            <div className="gv-ligne">
              {ETATS.map((etat) => (
                <button
                  key={etat}
                  type="button"
                  className={classes('gv-bouton', 'gv-bouton--petit')}
                  aria-pressed={etatsActifs.has(etat)}
                  style={etatsActifs.has(etat) ? undefined : { opacity: 0.45 }}
                  onClick={() => {
                    basculerEtat(etat);
                  }}
                >
                  {LIBELLE_ETAT_APPAREIL[etat]} ({formaterNombre(compteurs[etat])})
                </button>
              ))}
            </div>
          </div>
        </div>
      </Carte>

      {carte.error ? <MessageErreur erreur={carte.error} /> : null}

      <div className="gv-grille" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(280px, 1fr)' }}>
        <Carte sansPadding>
          {carte.isPending ? (
            <Chargement libelle="Chargement de la carte…" />
          ) : (
            <div style={{ height: '62vh', minHeight: 420 }}>
              <CarteGeo
                appareils={appareils}
                selectionne={selectionne?.id ?? null}
                surSelection={setSelectionne}
              />
            </div>
          )}
        </Carte>

        <Carte titre={selectionne ? selectionne.nom : 'Sélection'}>
          {selectionne ? (
            <div className="gv-pile">
              <div className="gv-ligne">
                <BadgeEtatAppareil etat={selectionne.statut_metier} />
                <BadgeConnectivite etat={selectionne.connectivite} />
                <PastilleNombre valeur={selectionne.alertes_actives} />
              </div>

              <Definitions
                entrees={[
                  { cle: 'Exploitation', valeur: selectionne.exploitation.nom },
                  {
                    cle: 'N° de série',
                    valeur: <span className="gv-mono">{selectionne.numero_serie_electrificateur}</span>,
                  },
                  {
                    cle: 'Position',
                    valeur: (
                      <span className="gv-mono gv-corps-s">
                        {formaterCoordonnees(selectionne.lat, selectionne.lon)}
                      </span>
                    ),
                  },
                  {
                    cle: 'Relevé le',
                    valeur: selectionne.position_at
                      ? formaterRelatif(selectionne.position_at)
                      : VALEUR_ABSENTE,
                  },
                  {
                    cle: 'Dernier message',
                    valeur: selectionne.dernier_message_at
                      ? formaterRelatif(selectionne.dernier_message_at)
                      : 'jamais vu',
                  },
                ]}
              />

              <div className="gv-ligne">
                <Bouton
                  variante="primaire"
                  onClick={() => {
                    naviguer(
                      `/appareils/${encodeURIComponent(selectionne.numero_serie_electrificateur)}`,
                    );
                  }}
                >
                  Ouvrir la fiche
                </Bouton>
                <Bouton
                  onClick={() => {
                    naviguer(`/exploitations/${selectionne.exploitation.id}`);
                  }}
                >
                  Exploitation
                </Bouton>
              </div>
            </div>
          ) : (
            <p className="gv-corps-s gv-secondaire">
              Cliquez un marqueur pour afficher son identité, sa position et l’accès à sa fiche de
              diagnostic.
            </p>
          )}
        </Carte>
      </div>
    </>
  );
}
